import {DataFetchingError, ObjectNotFoundError} from "@/shared/composable/service.errors.ts";
import {logErrorMessages} from "@vue/apollo-util";
import type {ApolloQueryResult} from "@apollo/client/core/types";
import {ResultPage} from "@/shared/components/ui/datagrid";
import {Page} from "@/shared/graphql/types";
import {ApolloError} from "@apollo/client";

export async function handleApiResponse<T>(promise: Promise<ApolloQueryResult<T>>): Promise<T> {
    const response = await handleApiErrors(promise)
    const result: T | null = extractGraphData(response.data) ?? null;
    if (result == null) throw new ObjectNotFoundError("Resource not found");
    return result;
}

export async function handleApiErrors<T>(promise: Promise<ApolloQueryResult<T>>): Promise<ApolloQueryResult<T>> {
    try {
        const response = await promise
        if (response.errors != undefined) {
            logErrorMessages(response.error);
            throw new Error(response.error.message);
        }
        return response;
    } catch (e) {
        resolveException(e)
    }
}

function resolveException(e: ApolloError): void {
    if (!e || !e.graphQLErrors || !Array.isArray(e.graphQLErrors)) return;

    for (const error of e.graphQLErrors) {
        if (error.extensions && error.extensions.classification === "DataFetchingException") {
            throw new DataFetchingError("A DataFetchingException was found.");
        } else if (error.extensions && error.extensions.classification === "OBJECT_NOT_FOUND") {
            throw new ObjectNotFoundError("The object could not be found.");
        }
    }
    throw new Error(e.message)
}

export function extractGraphData<T>(input: any, typeName?: string): T {
    if (input.__typename && input.__typename === typeName) {
        return input as T;
    }
    const keys = Object.keys(input).filter((name: string) => name != "__typename")
    if (keys.length > 1) {
        return input as T;
    }

    const firstKey = keys[0];
    const firstValue = input[firstKey];

    if (typeof firstValue === 'object' && firstValue !== null) {
        return clone(extractGraphData(firstValue as Record<string, any>, typeName));
    } else {
        return clone(firstValue);
    }
}

export function clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T
}

export function toResultPage<T>(page: Page): ResultPage<T> {
    return {
        numberOfPages: page.numberOfPages,
        pageNumber: page.pageNumber,
        pageSize: page.pageSize,
        totalNumberOfResults: page.totalNumberOfResults,
        //@ts-ignore
        results: page.results
    };
}

